import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Textarea,
  Toast,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { Send20Regular, Mic20Regular, Stop20Regular, Dismiss20Regular, SpinnerIos20Regular } from '@fluentui/react-icons';
import { getBrandConfig } from '../brands/brand-registry';
import { getAppConfig } from '../api/api';
import { useMicrophone } from '../hooks/useMicrophone';
import { useLiveSpeech } from '../hooks/useLiveSpeech';
import { InputMode } from '../api/models';
import { appendTranscriptValue, appendVoiceTranscript } from '../utils/voiceDraft';
import { InterpolatedPhoneText } from './InterpolatedPhoneText';

const useStyles = makeStyles({
  container: {
    maxWidth: '100%',
    margin: '0',
    padding: '0',
    position: 'relative',
  },
  hero: {
    textAlign: 'center',
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    maxWidth: '900px',
    width: '90%',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
    '@media (max-width: 600px)': {
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      width: '90%',
      maxWidth: '100%',
    },
    '@media (max-height: 800px)': {
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
    },
  },
  heading: {
    marginBottom: 'var(--welcome-title-margin-bottom, 8px)',
    fontSize: 'var(--welcome-title-size, 32px)',
    fontWeight: 'var(--welcome-title-weight, 700)',
    color: 'var(--welcome-title-color, var(--brand-80))',
    fontFamily: 'var(--font-family-base, "Segoe UI", "Helvetica Neue", Arial, sans-serif)',
    lineHeight: 'var(--welcome-title-line-height, 48px)',
    backgroundColor: 'transparent',
    padding: 0,
    display: 'block',
    '@media (max-width: 600px)': {
      marginBottom: 'var(--welcome-title-margin-bottom-mobile, 4px)',
      fontSize: 'var(--welcome-title-size-mobile, 28px)',
      lineHeight: 'var(--welcome-title-line-height-mobile, 24px)',
    },
    '@media (max-height: 800px)': {
      marginBottom: '4px',
      fontSize: '28px',
      lineHeight: '32px',
    },
  },
  subtitle: {
    color: 'var(--welcome-subtitle-color, #424242)',
    fontSize: 'var(--welcome-subtitle-size, 14px)',
    fontFamily: 'var(--font-family-base, "Segoe UI", "Helvetica Neue", Arial, sans-serif)',
    lineHeight: 'var(--welcome-subtitle-line-height, 21px)',
    fontWeight: 'var(--welcome-subtitle-weight, 400)',
    backgroundColor: 'transparent',
    padding: 0,
    display: 'block',
    marginBottom: 'var(--welcome-subtitle-margin-bottom, 20px)',
    '@media (max-width: 600px)': {
      marginBottom: 'var(--welcome-subtitle-margin-bottom-mobile, 10px)',
      fontSize: 'var(--welcome-subtitle-size-mobile, 16px)',
      lineHeight: 'var(--welcome-subtitle-line-height-mobile, 18px)',
    },
    '@media (max-height: 800px)': {
      marginBottom: '10px',
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxWidth: '1000px',
    width: '95%',
    margin: '0 auto',
    marginBottom: '40px',
    backgroundColor: 'var(--welcome-input-bg, var(--neutral-0))',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: 'var(--radius-field, 10px)',
    border: '1px solid var(--welcome-input-border, var(--border-primary))',
    boxShadow: 'none',
    '@media (max-width: 600px)': {
      width: '92%',
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
      marginBottom: '10px',
    },
    '@media (max-height: 800px)': {
      marginBottom: '10px',
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    '@media (max-width: 600px)': {
      gap: tokens.spacingHorizontalXS,
    },
  },
  micButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    gap: tokens.spacingVerticalXXS,
  },
  recordingMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    minHeight: '14px',
  },
  recordingTimer: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    minHeight: '14px',
    lineHeight: '14px',
  },
  recordingStatus: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    minHeight: '14px',
    lineHeight: '14px',
  },
  cancelRecordingButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorStatusDangerForeground1,
    minWidth: '24px',
    minHeight: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorStatusDangerForeground2,
      backgroundColor: tokens.colorStatusDangerBackground1,
      borderRadius: '9999px',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: '9999px',
    },
  },
  inlineClearInputButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    minWidth: '30px',
    minHeight: '30px',
    width: '30px',
    height: '30px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground3,
      borderRadius: '9999px',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: '9999px',
    },
  },
  inputDisclaimer: {
    fontSize: 'var(--disclaimer-font-size, 12px)',
    lineHeight: 'var(--disclaimer-line-height, 1.4)',
    color: 'var(--disclaimer-text-color, #666)',
    margin: 0,
    textAlign: 'center' as const,
    '@media (max-width: 600px)': {
      fontSize: 'var(--disclaimer-font-size-mobile, 11px)',
      lineHeight: 'var(--disclaimer-line-height-mobile, 1.3)',
    },
  },
  voiceUnavailable: {
    color: 'var(--text-secondary, #424242)',
    fontSize: '12px',
    lineHeight: '1.4',
    margin: 0,
    textAlign: 'left' as const,
  },
  inputDisclaimerLink: {
    color: 'var(--disclaimer-link-color, var(--brand-60))',
    fontWeight: 600,
    textDecoration: 'underline',
    '@media (max-width: 600px)': {
      fontSize: 'var(--disclaimer-link-size-mobile, 12px)',
      lineHeight: 'var(--disclaimer-link-line-height-mobile, 1.4)',
    },
    ':hover': {
      color: 'var(--disclaimer-link-hover-color, var(--brand-70))',
    },
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 'var(--welcome-input-font-size, 16px)',
    lineHeight: 'var(--welcome-input-line-height, 24px)',
    fontFamily: 'var(--font-family-base, "Segoe UI", sans-serif)',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    backgroundColor: 'transparent',
    minHeight: '48px',
    '&::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '& textarea::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '& textarea:disabled::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '@media (max-width: 600px)': {
      fontSize: 'var(--welcome-input-font-size-mobile, 14px)',
      lineHeight: 'var(--welcome-input-line-height-mobile, 20px)',
      width: '100%',
      minHeight: '44px',
    },
  },
  sendButton: {
    backgroundColor: 'var(--welcome-send-button-bg, var(--brand-60))',
    color: 'var(--text-on-brand, #FFFFFF)',
    border: 'none',
    borderRadius: 'var(--radius-full, 9999px)',
    minWidth: '48px',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--welcome-send-button-hover, var(--brand-70))',
      transform: 'scale(1.05)',
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
    },
  },
  micButtonConnecting: {
    backgroundColor: tokens.colorNeutralBackground4,
    cursor: 'wait',
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButton: {
    borderRadius: 'var(--radius-full, 9999px)',
    border: '2px solid var(--brand-60, #0078d4)',
    backgroundColor: 'var(--brand-160, #f0f6ff)',
    color: 'var(--brand-60, #0078d4)',
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground4,
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButtonRecording: {
    backgroundColor: tokens.colorStatusDangerBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
    animation: 'pulse-recording 1s ease-in-out infinite',
    '&:hover': {
      backgroundColor: tokens.colorStatusDangerBackground3,
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButtonProcessing: {
    backgroundColor: tokens.colorNeutralBackground4,
    cursor: 'wait',
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  tryAsking: {
    fontWeight: 'var(--welcome-label-weight, 600)',
    fontSize: 'var(--welcome-label-size, 14px)',
    paddingTop: 'var(--welcome-label-padding-top, 20px)',
    paddingBottom: 'var(--welcome-label-padding-bottom, 8px)',
    color: 'var(--welcome-text-color, var(--text-primary))',
    fontFamily: 'var(--font-family-base, "Segoe UI", "Helvetica Neue", Arial, sans-serif)',
    backgroundColor: 'transparent',
    display: 'block',
    marginBottom: tokens.spacingVerticalM,
    '@media (max-width: 600px)': {
      fontSize: 'var(--welcome-label-size-mobile, 16px)',
      paddingTop: 'var(--welcome-label-padding-top-mobile, 10px)',
      paddingBottom: 'var(--welcome-label-padding-bottom-mobile, 4px)',
    },
    '@media (max-height: 800px)': {
      paddingTop: '10px',
      marginBottom: '8px',
    },
  },
  pills: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    maxWidth: '100%',
    width: '100%',
    marginTop: tokens.spacingVerticalM,
    marginBottom: 'var(--welcome-pills-margin-bottom, 40px)',
    '@media (max-width: 600px)': {
      marginBottom: 'var(--welcome-pills-margin-bottom-mobile, 20px)',
      gap: tokens.spacingVerticalXXS,
    },
    '@media (max-height: 800px)': {
      marginBottom: '16px',
      marginTop: '8px',
    },
  },
  pill: {
    minWidth: '320px',
    minHeight: '44px',  // WCAG AAA: Minimum touch target (2.5.5)
    width: 'auto',
    maxWidth: '480px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--welcome-pill-bg, var(--neutral-0))',
    color: 'var(--welcome-pill-text-color, var(--text-primary))',
    fontFamily: 'var(--font-family-base, "Segoe UI", "Helvetica Neue", Arial, sans-serif)',
    fontSize: 'var(--chip-question-size, 14px)',
    fontWeight: 'var(--chip-question-weight, 400)',
    fontStyle: 'var(--chip-question-style, normal)',
    lineHeight: '1.5', // WCAG AAA: 1.5× line height (1.4.8)
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: 'var(--welcome-pill-border-radius, 9999px)',
    border: 'var(--welcome-pill-border, 1px solid var(--border-primary))',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--welcome-pill-hover-bg, var(--neutral-30))',
      transform: 'translateY(-1px)',
    },
    '@media (max-width: 600px)': {
      minWidth: '280px',
      minHeight: '40px',
      fontSize: 'var(--chip-question-size-mobile, 12px)',
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    },
  }
});

export default function HeroWelcome({ onPick, onVoiceTranscription }: { onPick: (q: string, inputMode?: InputMode) => void; onVoiceTranscription?: (text: string) => void }) {
  const s = useStyles();
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const voiceDraftOriginalRef = useRef<string | null>(null);
  const hasVoiceInputRef = useRef(false);
  const preRecordingValueRef = useRef<string>('');
  
  // Get brand configuration for dynamic content
  const brandConfig = getBrandConfig();
  
  // Prefer API-provided suggested questions (from token config), fall back to brand config
  const apiConfig = getAppConfig();
  const questions = (apiConfig.suggested_questions && apiConfig.suggested_questions.length > 0)
    ? apiConfig.suggested_questions
    : brandConfig.hero.suggestedQuestions;
  const showInputField = brandConfig.hero.showInputField ?? true; // Default to true
  const inputFieldPosition = brandConfig.hero.inputFieldPosition ?? 'top';
  const [srAnnouncement, setSrAnnouncement] = useState<string>('');
  const voiceToasterId = useId('voice-input-toaster');
  const { dispatchToast } = useToastController(voiceToasterId);
  const sttSilenceTimeoutMs = apiConfig.stt_silence_timeout_ms ?? 3000;
  const isMicSupported = typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined';

  // Get speech provider from brand config
  const speechProvider = (brandConfig.features as { speechProvider?: 'openai' | 'abair' }).speechProvider || 'openai';
  const abairWebSocketUrl = (brandConfig.features as { abairWebSocketUrl?: string }).abairWebSocketUrl || '';
  const isAbairProvider = speechProvider === 'abair' && abairWebSocketUrl;
  const voiceInputLabels = brandConfig.chatInput.voiceInput;

  const showNoSpeechToast = (message: string) => {
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: 'warning', timeout: 5000 },
    );
  };

  const applyVoiceTranscript = (text: string) => {
    const result = appendVoiceTranscript(preRecordingValueRef.current, text, {
      hasVoiceInput: hasVoiceInputRef.current,
      pureVoiceValue: voiceDraftOriginalRef.current,
    });
    setInputValue(result.value);
    onVoiceTranscription?.(text);
    hasVoiceInputRef.current = result.state.hasVoiceInput;
    voiceDraftOriginalRef.current = result.state.pureVoiceValue;
  };

  // Live speech hook for Abair (real-time text display)
  const liveSpeech = useLiveSpeech({
    provider: 'abair',
    websocketUrl: abairWebSocketUrl,
    onFinalTranscript: applyVoiceTranscript,
    onError: (error) => {
      setSrAnnouncement(error);
    },
    onAnnounce: (message) => {
      setSrAnnouncement(message);
    },
    messages: voiceInputLabels,
  });

  // Batch microphone hook for OpenAI (traditional flow)
  const batchMicrophone = useMicrophone({
    onTranscription: applyVoiceTranscript,
    onError: (error) => {
      if (error === voiceInputLabels.noSpeechDetectedMessage) {
        setSrAnnouncement('');
        showNoSpeechToast(error);
        return;
      }
      setSrAnnouncement(error);
    },
    onAnnounce: (message) => {
      setSrAnnouncement(message);
    },
    silenceTimeoutMs: sttSilenceTimeoutMs,
    messages: voiceInputLabels,
  });

  // Unified interface
  const micState = isAbairProvider ? liveSpeech.state : batchMicrophone.micState;
  const timerDisplay = isAbairProvider ? liveSpeech.timerDisplay : batchMicrophone.timerDisplay;
  const partialText = isAbairProvider ? liveSpeech.partialText : '';
  const recordingStatusText = micState === 'connecting'
    ? voiceInputLabels.preparingStatus
    : micState === 'recording'
      ? voiceInputLabels.listeningStatus
      : '';
  const isVoiceInputLocked = micState === 'connecting' || micState === 'recording' || micState === 'processing';
  const isMicTemporarilyUnavailable = micState === 'connecting' || micState === 'processing';

  const toggleRecording = useCallback(() => {
    if (micState === 'idle') {
      setSrAnnouncement('');
      preRecordingValueRef.current = inputValue;
    }

    if (isAbairProvider) {
      if (liveSpeech.isRecording) {
        liveSpeech.stopRecording();
      } else if (liveSpeech.state === 'idle') {
        liveSpeech.startRecording();
      }
      return;
    }

    batchMicrophone.toggleRecording();
  }, [
    batchMicrophone.toggleRecording,
    inputValue,
    isAbairProvider,
    liveSpeech.isRecording,
    liveSpeech.startRecording,
    liveSpeech.state,
    liveSpeech.stopRecording,
    micState,
  ]);

  const cancelRecording = useCallback(() => {
    if (isAbairProvider) {
      liveSpeech.cancelRecording();
    } else {
      batchMicrophone.cancelRecording();
    }
    setInputValue(preRecordingValueRef.current);
  }, [batchMicrophone.cancelRecording, isAbairProvider, liveSpeech.cancelRecording]);

  useEffect(() => {
    if (micState !== 'recording' && micState !== 'connecting') {
      return;
    }

    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelRecording();
      }
    };

    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [cancelRecording, micState]);

  useEffect(() => {
    const onShortcutKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === 'm' && !event.repeat && isMicSupported) {
        event.preventDefault();
        toggleRecording();
      }
    };

    window.addEventListener('keydown', onShortcutKeyDown);
    return () => window.removeEventListener('keydown', onShortcutKeyDown);
  }, [isMicSupported, toggleRecording]);
  


  // Update input with partial text during live recording
  useEffect(() => {
    if (isAbairProvider && liveSpeech.isRecording && partialText) {
      setInputValue(appendTranscriptValue(preRecordingValueRef.current, partialText));
    }
  }, [isAbairProvider, liveSpeech.isRecording, partialText]);

  // Auto-focus input on initial load for better keyboard accessibility (WCAG 2.4.3)
  // Focus the welcome input when shown, regardless of position (top or bottom)
  useEffect(() => {
    if (showInputField && textareaRef.current) {
      // Small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showInputField]);

  const handlePillClick = (question: string) => {
    if (isVoiceInputLocked) {
      return;
    }
    voiceDraftOriginalRef.current = null;
    hasVoiceInputRef.current = false;
    setSrAnnouncement('');
    onPick(question, 'Text');
  };

  const handleSend = () => {
    if (isVoiceInputLocked) {
      return;
    }
    if (inputValue.trim()) {
      const trimmed = inputValue.trim();
      let inputMode: InputMode = 'Text';
      if (hasVoiceInputRef.current) {
        inputMode = voiceDraftOriginalRef.current === trimmed ? 'Voice' : 'Voice_edited';
      }
      onPick(trimmed, inputMode);
      voiceDraftOriginalRef.current = null;
      hasVoiceInputRef.current = false;
      setSrAnnouncement('');
      setInputValue('');
    }
  };

  const handleClearInput = () => {
    if (isVoiceInputLocked) {
      return;
    }
    voiceDraftOriginalRef.current = null;
    hasVoiceInputRef.current = false;
    setSrAnnouncement('');
    setInputValue('');
  };

  const handleInputChange = (nextValue: string) => {
    setSrAnnouncement('');
    if (!nextValue.trim()) {
      voiceDraftOriginalRef.current = null;
      hasVoiceInputRef.current = false;
    }
    setInputValue(nextValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
    if (event.key === 'Escape' && (micState === 'recording' || micState === 'connecting')) {
      event.preventDefault();
      event.stopPropagation();
      cancelRecording();
    }
  };

  const handleKeyDownPill = (event: React.KeyboardEvent, question: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePillClick(question);
    }
  };

  const inputField = showInputField ? (
    <div className={s.inputContainer}>
      <div className={s.inputRow}>
        {/* Screen reader live region */}
        <span aria-live="polite" aria-atomic="true" className="sr-only">
          {srAnnouncement}
        </span>

        {brandConfig.features.enableVoiceInput && isMicSupported && (
          <div className={s.micButtonContainer}>
            <span aria-hidden="true" className={s.recordingStatus}>
              {recordingStatusText}
            </span>
            <button
              className={`${s.micButton} ${micState === 'recording' ? s.micButtonRecording : ''} ${micState === 'processing' ? s.micButtonProcessing : ''} ${micState === 'connecting' ? s.micButtonConnecting : ''}`}
              onClick={toggleRecording}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!e.repeat) {
                    toggleRecording();
                  }
                }
              }}
              aria-disabled={isMicTemporarilyUnavailable}
              aria-keyshortcuts="Alt+M"
              aria-label={voiceInputLabels.startRecordingAriaLabel}
            >
              {
                micState === 'connecting' ? <SpinnerIos20Regular /> :
                micState === 'processing' ? <SpinnerIos20Regular /> :
                micState === 'recording' ? <Stop20Regular /> :
                <Mic20Regular />
              }
            </button>
            <div className={s.recordingMetaRow}>
              <span aria-hidden="true" className={s.recordingTimer}>
                {(micState === 'recording' || micState === 'connecting') ? timerDisplay : ''}
              </span>
              {(micState === 'recording' || micState === 'connecting') && (
                <button
                  type="button"
                  className={s.cancelRecordingButton}
                  onClick={cancelRecording}
                  aria-keyshortcuts="Escape"
                  aria-label={voiceInputLabels.cancelRecordingAriaLabel}
                  title={voiceInputLabels.cancelRecordingTitle}
                >
                  <Dismiss20Regular />
                </button>
              )}
            </div>
          </div>
        )}

        <Textarea
          ref={textareaRef}
          className={s.input}
          placeholder={brandConfig.chatInput.placeholder}
          aria-label={brandConfig.chatInput.ariaLabel || `Type your message to ${brandConfig.appName} chatbot`}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          readOnly={isVoiceInputLocked}
          resize="none"
          rows={1}
          maxLength={500}
          style={{
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
          }}
        />

        {inputValue.trim().length > 0 && !isVoiceInputLocked && (
          <button
            type="button"
            className={s.inlineClearInputButton}
            onClick={handleClearInput}
            aria-label={voiceInputLabels.clearInputAriaLabel}
            title={voiceInputLabels.clearInputTitle}
          >
            <Dismiss20Regular />
          </button>
        )}

        <button
          className={s.sendButton}
          onClick={handleSend}
          disabled={isVoiceInputLocked}
          aria-disabled={isVoiceInputLocked}
          aria-label={brandConfig.chatInput.sendButton}
        >
          <Send20Regular />
        </button>
      </div>
      {brandConfig.features.enableVoiceInput && !isMicSupported && (
        <p className={s.voiceUnavailable} role="status">
          {voiceInputLabels.unsupportedBrowserMessage}
        </p>
      )}
      {brandConfig.hero.disclaimerText && (
        <p className={s.inputDisclaimer}>
          {brandConfig.hero.disclaimerText}
          {brandConfig.hero.disclaimerLinkText && brandConfig.hero.disclaimerLinkUrl && (
            <a
              href={brandConfig.hero.disclaimerLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={s.inputDisclaimerLink}
            >
              {brandConfig.hero.disclaimerLinkText}
            </a>
          )}
          <InterpolatedPhoneText
            text={brandConfig.hero.disclaimerAfterLinkText}
            phoneNumber={brandConfig.hero.disclaimerPhoneNumber}
            linkClassName={s.inputDisclaimerLink}
          />
        </p>
      )}
    </div>
  ) : null;

  // Render formatted title parts if available, otherwise plain text
  const renderFormattedTitle = () => {
    const parts = brandConfig.titleFormatting?.heroTitleParts;
    if (parts && Array.isArray(parts)) {
      return parts.map((part: { text: string; bold: boolean }, index: number) => 
        part.bold ? <strong key={index}>{part.text}</strong> : <span key={index}>{part.text}</span>
      );
    }
    return brandConfig.hero.title;
  };

  return (
    <div className={s.container}>
      <Toaster toasterId={voiceToasterId} position="top-end" />
      <div className={s.hero}>
        <h1 className={s.heading}>
          {renderFormattedTitle()}
        </h1>
        <p className={s.subtitle}>
          {brandConfig.hero.subtitle}
        </p>
        
        {/* Input Field - Top Position */}
        {inputFieldPosition === 'top' && inputField}

        <div className={s.tryAsking}>{brandConfig.hero.tryAskingLabel}</div>
        <div className={s.pills}>
          {questions.map(question => (
            <button
              key={question}
              onClick={() => handlePillClick(question)}
              onKeyDown={(e) => handleKeyDownPill(e, question)}
              disabled={isVoiceInputLocked}
              aria-disabled={isVoiceInputLocked}
              aria-label={`${brandConfig.followUp.ariaLabelPrefix}${question}`}
              className={s.pill}
            >
              {question}
            </button>
          ))}
        </div>
        
        {/* Input Field - Bottom Position */}
        {inputFieldPosition === 'bottom' && inputField}
        

      </div>
    </div>
  );
}
